import { useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { DataTableColumn } from 'mantine-datatable';
// mantine
import { Spoiler } from '@mantine/core';
import { useDataTableColumns } from 'mantine-datatable';
// components
import { AudioPlayerControl, NumberFormatter } from '@newo/ui-kit';
import { ISession, TSessionsData } from '@newo/api/gateway';
import { getBrowserGMTTimezone } from '@newo/toolkit/datetime';
import { getRecordingName } from '@newo/toolkit/bff';
import DateTime from '@/modules/DateTime/components/DateTime';
import ResultSuccessSelect from '@/pages/Sessions/components/ResultSuccessSelect';
import AIResultSuccess from '@/pages/Sessions/components/AIResultSuccess';
import CommentPopover from '@/pages/Sessions/components/CommentPopover';
import PenaltyInput from '@/pages/Sessions/components/PenaltyInput';
import SessionIdCell from '@/pages/Sessions/components/SessionIdCell/SessionIdCell';
import UniversalToggle from '@/pages/Sessions/components/UniversalToggle';
import Actions from '@/pages/Sessions/components/Actions';
import UTMs from '@/pages/Sessions/components/UTMs';
// utils
import {
    SESSIONS_TABLE_STORE_KEY,
    SESSIONS_TABLE_VERSION
} from '@/pages/Sessions/Sessions.utils.ts';
import { getCssColumn } from '@/pages/Sessions/Sessions.cssColumn.tsx';
import useSessionsStore from '@/pages/Sessions/Sessions.store.ts';
import { useSessionUpdate } from '@/services/sessions/sessions.queries.ts';
import { DATE_FORMAT } from '@/const';

const SPOILER_MAX_HEIGHT = 150;
const PERCENT_MULTIPLIER = 100;

type SessionTableColumn = DataTableColumn<ISession> & { accessor: string };

export const useColumns = (sessionIdEdgeLength: number) => {
    const { hiddenColumns, setHiddenColumns, version, setVersion } =
        useSessionsStore(
            useShallow((state) => ({
                hiddenColumns: state.hiddenColumns,
                setHiddenColumns: state.setHiddenColumns,
                version: state.version,
                setVersion: state.setVersion
            }))
        );

    const { mutate: updateSession } = useSessionUpdate();

    const handleSave = useCallback(
        (sessionId: string, data: TSessionsData) => {
            updateSession({ sessionId, data });
        },
        [updateSession]
    );

    const columns: SessionTableColumn[] = [
        {
            title: 'Is Archived',
            accessor: 'is_archived',
            width: 125,
            draggable: true,
            resizable: false,
            hidden: false,
            render: (session) => {
                return (
                    <UniversalToggle
                        fieldName="is_archived"
                        isChecked={session.is_archived}
                        sessionId={session.id}
                        onSave={handleSave}
                    />
                );
            }
        },
        {
            title: `Date / Time (${getBrowserGMTTimezone()})`,
            accessor: 'datetime',
            cellsClassName: 'whitespace-nowrap',
            render: (session) => (
                <DateTime
                    format={DATE_FORMAT.SHORT_12H}
                    value={session.ended_at ?? ''}
                />
            )
        },
        {
            title: 'Session Type',
            accessor: 'session_type',
            width: 200,
            draggable: true,
            resizable: true,
            hidden: false,
            cellsClassName: 'break-words',
            render: (session) => {
                return session.session_type;
            }
        },

        {
            title: 'Is Lead',
            accessor: 'is_lead',
            width: 100,
            draggable: true,
            resizable: false,
            hidden: false,
            render: (session) => {
                return (
                    <UniversalToggle
                        fieldName="is_lead"
                        isChecked={session.is_lead}
                        sessionId={session.id}
                        onSave={handleSave}
                    />
                );
            }
        },
        {
            title: 'Is Opportunity',
            accessor: 'is_opportunity',
            width: 125,
            draggable: true,
            resizable: false,
            hidden: false,
            render: (session) => {
                return (
                    <UniversalToggle
                        fieldName="is_opportunity"
                        isChecked={session.is_opportunity}
                        sessionId={session.id}
                        onSave={handleSave}
                    />
                );
            }
        },
        {
            title: 'ASR',
            accessor: 'asr',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 100,
            cellsClassName: 'text-md font-semibold text-green-6',
            render: (session) => {
                return <NumberFormatter prefix="$" value={session.asr} />;
            }
        },
        {
            title: 'Is Test',
            accessor: 'is_test',
            draggable: true,
            resizable: false,
            hidden: false,
            width: 100,
            render: (session) => {
                return (
                    <UniversalToggle
                        fieldName="is_test"
                        isChecked={session.is_test}
                        sessionId={session.id}
                        onSave={handleSave}
                    />
                );
            }
        },
        {
            title: 'Is Todo',
            accessor: 'is_todo',
            draggable: true,
            resizable: false,
            hidden: false,
            width: 100,
            render: (session) => {
                return (
                    <UniversalToggle
                        fieldName="is_todo"
                        isChecked={session.is_todo}
                        sessionId={session.id}
                        onSave={handleSave}
                    />
                );
            }
        },
        {
            title: 'Contact',
            accessor: 'contact',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 150,
            cellsClassName: 'truncate',
            render: ({ persona }) => {
                return persona.name ?? 'Unknown';
            }
        },
        {
            title: 'Contact Information',
            accessor: 'contact_information',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 200,
            cellsClassName: 'truncate',
            render: ({ persona }) => {
                return persona.contact_information;
            }
        },
        {
            title: 'Summary',
            accessor: 'summary',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 350,
            render: ({ arguments: args, persona, recordings }) => {
                return (
                    <>
                        <Spoiler
                            hideLabel="Hide"
                            maxHeight={SPOILER_MAX_HEIGHT}
                            showLabel="Show more"
                        >
                            <div className="text-xs">{args.summary}</div>
                        </Spoiler>

                        <div className="flex flex-wrap gap-2 pt-2">
                            {recordings.map((recording, index) => (
                                <AudioPlayerControl
                                    key={recording.play_url}
                                    label={getRecordingName(
                                        `Recording #${index + 1}`,
                                        recording.duration
                                    )}
                                    size="xs"
                                    trackName={persona.external_id}
                                    trackUrl={recording.play_url}
                                    type="button"
                                />
                            ))}
                        </div>
                    </>
                );
            }
        },

        {
            title: 'Lead Value',
            accessor: 'lead_value',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 125,
            render: (session) => {
                return (
                    <NumberFormatter prefix="$" value={session.lead_value} />
                );
            }
        },
        {
            title: 'Conversion Rate',
            accessor: 'conversion_rate',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 125,
            render: (session) => {
                return (
                    <NumberFormatter
                        suffix="%"
                        value={session.conversion_rate * PERCENT_MULTIPLIER}
                    />
                );
            }
        },
        {
            title: 'Recognition',
            accessor: 'asr_recognition',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 125,
            render: (session) => {
                return (
                    <NumberFormatter
                        suffix="%"
                        value={session.asr_recognition * PERCENT_MULTIPLIER}
                    />
                );
            }
        },
        {
            title: 'Session Length',
            accessor: 'session_length',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 100,
            cellsClassName: 'capitalize'
        },
        {
            title: 'Auto assessment',
            accessor: 'agent_success',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 150,
            render: (session) => {
                return <AIResultSuccess value={session.agent_success} />;
            }
        },
        {
            title: 'Human assessment',
            accessor: 'agent_success_human',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 150,
            render: (session) => {
                return (
                    <ResultSuccessSelect
                        sessionId={session.id}
                        value={session.agent_success_human}
                        onSave={handleSave}
                    />
                );
            }
        },

        {
            title: 'Conversation Quality',
            accessor: 'conversation_penalty',
            draggable: true,
            resizable: true,
            hidden: false,
            render: (session) => {
                return (
                    <PenaltyInput
                        sessionId={session.id}
                        value={session.conversation_penalty}
                        onSave={handleSave}
                    />
                );
            }
        },
        getCssColumn(),

        {
            title: 'Working Hours',
            accessor: 'workingHours',
            draggable: true,
            resizable: true,
            hidden: false,
            cellsClassName: 'truncate',
            render: ({ arguments: args }) => {
                return args.workingHours;
            }
        },
        {
            title: 'Comment',
            accessor: 'comment',
            width: 300,
            draggable: true,
            resizable: true,
            hidden: false,
            cellsClassName: 'break-words',
            render: ({ comment, id }) => {
                return (
                    <CommentPopover
                        comment={comment}
                        sessionId={id}
                        onSave={handleSave}
                    />
                );
            }
        },
        {
            title: 'Units',
            accessor: 'units_total',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 125,
            render: (session) => {
                return <NumberFormatter value={session.units_total} />;
            }
        },
        {
            title: 'Integration',
            accessor: 'integration',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 150,
            cellsClassName: 'truncate',
            render: (session) => {
                return session.integration_idn;
            }
        },
        {
            title: 'External ID',
            accessor: 'externalId',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 200,
            cellsClassName: 'truncate',
            render: ({ persona }) => {
                return persona.external_id;
            }
        },
        {
            title: 'Actual Result',
            accessor: 'actual_result',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 400,
            render: (session) => {
                return (
                    <Spoiler
                        hideLabel="Hide"
                        maxHeight={SPOILER_MAX_HEIGHT}
                        showLabel="Show more"
                    >
                        <div className="whitespace-pre-wrap">
                            {session.actual_result}
                        </div>
                    </Spoiler>
                );
            }
        },
        {
            title: 'Expected Result',
            accessor: 'expected_result',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 400,
            render: (session) => {
                return (
                    <Spoiler
                        hideLabel="Hide"
                        maxHeight={SPOILER_MAX_HEIGHT}
                        showLabel="Show more"
                    >
                        <div className="whitespace-pre-wrap">
                            {session.expected_result}
                        </div>
                    </Spoiler>
                );
            }
        },

        {
            title: 'Session Id',
            accessor: 'id',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 200,
            cellsClassName: 'truncate',
            render: (session) => {
                return (
                    <SessionIdCell
                        edgeLength={sessionIdEdgeLength}
                        sessionId={session.id}
                    />
                );
            }
        },

        {
            title: 'UTMs',
            accessor: 'utms',
            draggable: true,
            resizable: true,
            hidden: false,
            width: 200,
            render: ({ arguments: args }) => {
                return <UTMs value={args.utms} />;
            }
        },
        {
            title: 'Actions',
            accessor: 'actions',
            render: (session) => {
                return <Actions session={session} />;
            }
        }
    ];

    const mappedColumns = columns.map((column) => ({
        ...column,
        hidden: hiddenColumns.includes(column.accessor)
    }));

    const { effectiveColumns, resetColumnsWidth, resetColumnsOrder } =
        useDataTableColumns<ISession>({
            key: SESSIONS_TABLE_STORE_KEY,
            columns: mappedColumns
        });

    const resetColumns = useCallback(() => {
        setHiddenColumns([]);
        resetColumnsWidth();
        resetColumnsOrder();
    }, [setHiddenColumns, resetColumnsWidth, resetColumnsOrder]);

    useEffect(() => {
        if (version !== SESSIONS_TABLE_VERSION) {
            setVersion(SESSIONS_TABLE_VERSION);
            resetColumns();
        }
    }, [version, resetColumns, setVersion]);

    return { effectiveColumns, resetColumns };
};
